/**
 * Text handling for rendering an email conversation.
 *
 * Every reply a mail client sends carries the whole history under it, quoted. Rendered raw, the
 * third message in a thread repeats the first two and the conversation reads worse the longer it
 * gets. These helpers find where the fresh writing stops so the page can fold the history away,
 * and normalise subjects so "Re: Re: Booking" is not presented as new information.
 */

// Each pattern marks the first line of quoted or forwarded history. They are heuristics: mail
// clients disagree with each other, so the aim is the common shapes rather than completeness,
// and a message matching none of them is simply shown in full.
const QUOTE_STARTS: readonly RegExp[] = [
  /^>/, // classic quoting, every client
  /^On .{0,200}wrote:\s*$/, // Gmail and Apple Mail introducer
  /^-{2,}\s*Original Message\s*-{2,}$/i, // Outlook
  /^_{8,}\s*$/, // the Outlook divider above its header block
  /^-{2,}\s*Forwarded message\s*-{2,}$/i, // Gmail forward
];

export interface SplitBody {
  /** The fresh writing, shown at full weight. */
  reply: string;
  /** The quoted history under it, folded away. Empty when there is nothing worth folding. */
  quoted: string;
}

/** Splits an email text body into the fresh reply and the quoted history under it. */
export function splitQuoted(text: string): SplitBody {
  const lines = text.split(/\r?\n/);
  let cut = -1;
  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index] ?? "";
    if (QUOTE_STARTS.some((pattern) => pattern.test(line))) {
      cut = index;
      break;
    }
    // The Outlook reply header pasted without its divider: "From:" with "Sent:", "Date:",
    // "To:" or "Subject:" close under it. "From:" alone is not enough, because an ordinary
    // sentence can start with it.
    if (/^From:\s/.test(line)) {
      const following = lines.slice(index + 1, index + 4);
      if (following.some((candidate) => /^(Sent|Date|To|Subject):\s/.test(candidate))) {
        cut = index;
        break;
      }
    }
  }
  // No quote found, or the message IS the quote from its first line: folding would hide the
  // message itself, so both cases show everything.
  if (cut <= 0) return { reply: text.trim(), quoted: "" };
  // A wrapped introducer leaves "... wrote:" alone on the line above the first "> " line;
  // it belongs to the history it introduces.
  let start = cut;
  if (/^>/.test(lines[cut] ?? "") && /wrote:\s*$/.test((lines[cut - 1] ?? "").trim())) {
    start = cut - 1;
  }
  const reply = lines.slice(0, start).join("\n").trim();
  const quoted = lines.slice(start).join("\n").trim();
  if (reply === "") return { reply: text.trim(), quoted: "" };
  return { reply, quoted };
}

/**
 * Strips the reply and forward prefixes a subject accumulates, for comparing against the
 * thread's own subject. A message whose subject is only "Re: <thread subject>" says nothing
 * the page header has not already said.
 */
export function normalizeSubject(subject: string): string {
  let value = subject.trim();
  const prefix = /^(re|fw|fwd)\s*:\s*/i;
  while (prefix.test(value)) value = value.replace(prefix, "");
  return value.toLowerCase();
}
